import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Bookmark, BookmarkBorder } from '@mui/icons-material';
import { LD } from './tokens';

interface MobileActionBarProps {
  ctaText: string;
  ctaIcon: React.ReactNode;
  ctaDisabled: boolean;
  onCtaClick: () => void;
  /** Omitted where Save does not apply (assumable search results). */
  onSaveClick?: () => void;
  isSaved: boolean;
  saveDisabled: boolean;
  saveLabel: string;
  footnote: string;
}

/** The phone's sticky bottom bar: the tour button, Save, and what it costs. */
const MobileActionBar: React.FC<MobileActionBarProps> = ({
  ctaText,
  ctaIcon,
  ctaDisabled,
  onCtaClick,
  onSaveClick,
  isSaved,
  saveDisabled,
  saveLabel,
  footnote,
}) => (
  <Box
    data-testid="mobile-action-bar"
    sx={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1300,
      bgcolor: 'white',
      borderTop: `1px solid ${LD.line}`,
      px: 2,
      pt: '12px',
      pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    }}
  >
    <Box sx={{ display: 'flex', gap: '10px' }}>
      <Button
        variant="contained"
        disableElevation
        fullWidth
        startIcon={ctaIcon}
        onClick={onCtaClick}
        disabled={ctaDisabled}
        sx={{
          flex: 1,
          height: 52,
          borderRadius: '10px',
          fontSize: '16.5px',
          fontWeight: 700,
          textTransform: 'none',
          bgcolor: LD.primary,
          '&:hover': { bgcolor: LD.primaryDark },
          '& .MuiButton-startIcon svg': { fontSize: 20 },
        }}
      >
        {ctaText}
      </Button>
      {onSaveClick && (
        <IconButton
          onClick={onSaveClick}
          disabled={saveDisabled}
          aria-label={saveLabel}
          aria-pressed={isSaved}
          sx={{
            width: 52,
            height: 52,
            flexShrink: 0,
            borderRadius: '10px',
            border: `1.5px solid ${isSaved ? LD.green : LD.saveBorder}`,
            color: isSaved ? LD.green : LD.ink2,
            '&.Mui-disabled': { color: isSaved ? LD.green : LD.muted2 },
          }}
        >
          {isSaved ? (
            <Bookmark sx={{ fontSize: 24 }} />
          ) : (
            <BookmarkBorder sx={{ fontSize: 24 }} />
          )}
        </IconButton>
      )}
    </Box>
    <Typography
      sx={{
        fontSize: '12.5px',
        color: LD.muted,
        textAlign: 'center',
        mt: '8px',
        lineHeight: 1.4,
      }}
    >
      {footnote}
    </Typography>
  </Box>
);

export default MobileActionBar;
